import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/* ------------------------------------------------------------------ */
/* Avisos (toasts)                                                      */
/* ------------------------------------------------------------------ */

type ToastTone = 'ok' | 'error' | 'info';
interface Toast { id: number; message: string; tone: ToastTone }

const ToastContext = createContext<(message: string, tone?: ToastTone) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((message: string, tone: ToastTone = 'info') => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current, { id, message, tone }]);
    window.setTimeout(() => setToasts((current) => current.filter((t) => t.id !== id)), 5200);
  }, []);

  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="toasts" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast ${toast.tone}`}>{toast.message}</div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = (): ((message: string, tone?: ToastTone) => void) => useContext(ToastContext);

/* ------------------------------------------------------------------ */
/* Componentes básicos                                                  */
/* ------------------------------------------------------------------ */

export function Tag({ tone = 'muted', children }: { tone?: string; children: ReactNode }): JSX.Element {
  return <span className={`tag ${tone}`}>{children}</span>;
}

export function Card({
  title, subtitle, actions, children, className = '',
}: { title?: ReactNode; subtitle?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }): JSX.Element {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && (
        <header className="card-head">
          <div>
            {title && <h2 className="card-title">{title}</h2>}
            {subtitle && <p className="card-sub">{subtitle}</p>}
          </div>
          {actions}
        </header>
      )}
      {children}
    </section>
  );
}

export function Field({
  label, hint, children, id,
}: { label: string; hint?: ReactNode; children: ReactNode; id?: string }): JSX.Element {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children}
      {hint && <span className="hint">{hint}</span>}
    </div>
  );
}

export function Checkbox({
  checked, onChange, label,
}: { checked: boolean; onChange: (value: boolean) => void; label: ReactNode }): JSX.Element {
  return (
    <label className="checkline">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

export function StatTile({
  label, value, meta, tone,
}: { label: string; value: ReactNode; meta?: ReactNode; tone?: string }): JSX.Element {
  return (
    <div className="stat">
      <p className="stat-label">{label}</p>
      <p className="stat-value" style={tone ? { color: tone } : undefined}>{value}</p>
      {meta && <p className="stat-meta">{meta}</p>}
    </div>
  );
}

export function EmptyState({
  title, description, action,
}: { title: string; description: string; action?: ReactNode }): JSX.Element {
  return (
    <div className="empty">
      <h3>{title}</h3>
      <p>{description}</p>
      {action}
    </div>
  );
}

export function Loading({ label = 'Carregando…' }: { label?: string }): JSX.Element {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', color: 'var(--text-3)', padding: '22px 0' }}>
      <span className="spinner-inline" /> {label}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }): JSX.Element {
  return (
    <div className="alert error">
      <span>⚠</span>
      <div style={{ flex: 1 }}>
        <div>{message}</div>
        {onRetry && (
          <button type="button" className="btn ghost small" style={{ marginTop: 10 }} onClick={onRetry}>
            Tentar novamente
          </button>
        )}
      </div>
    </div>
  );
}

export function Modal({
  title, children, footer, onClose, wide = false,
}: { title: string; children: ReactNode; footer?: ReactNode; onClose: () => void; wide?: boolean }): JSX.Element {
  useEffect(() => {
    const handler = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className={`modal ${wide ? 'wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head">
          <h2 className="page-title" style={{ fontSize: 17 }}>{title}</h2>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

export function CopyButton({ value, label = 'Copiar' }: { value: string; label?: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  const toast = useToast();
  return (
    <button
      type="button"
      className="btn secondary small"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
        } catch {
          // Fallback para navegadores sem Clipboard API disponível.
          const area = document.createElement('textarea');
          area.value = value;
          document.body.appendChild(area);
          area.select();
          document.execCommand('copy');
          area.remove();
        }
        setCopied(true);
        toast('Copiado para a área de transferência.', 'ok');
        window.setTimeout(() => setCopied(false), 1800);
      }}
    >
      {copied ? 'Copiado ✓' : label}
    </button>
  );
}

export function Tabs({
  tabs, active, onChange,
}: { tabs: { id: string; label: string }[]; active: string; onChange: (id: string) => void }): JSX.Element {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          className="tab"
          aria-selected={tab.id === active}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Hook de carregamento de dados                                        */
/* ------------------------------------------------------------------ */

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useAsync<T>(loader: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loader()
      .then((result) => { if (!cancelled) setData(result); })
      .catch((err: Error) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // `loader` é recriado a cada render por desenho: as dependências reais são `deps`.
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return useMemo(() => ({ data, loading, error, reload }), [data, loading, error, reload]);
}

export function useDebounced<T>(value: T, delay = 350): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}
